import React from 'react';
import { StyleSheet, View } from 'react-native';
import { theme } from '@sweetgreen/garnish';

import { PaymentMethodListItem } from '@order/components';
import { getCreditCardIcon } from '@order/constants';
import type { PaymentMethod } from '@order/graphql';
import { CardExpirationState } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

export const PaymentMethodSelector = (props: PaymentMethodSelectorProps) => {
  const { paymentMethods, onSelect } = props;

  const { t } = useLocalizationContext();

  const paymentMethod =
    paymentMethods?.find((method) => method.isDefault) ?? paymentMethods?.[0];

  const notice = usePaymentMethodExpirationMessage(
    paymentMethod?.expirationState,
  );

  return (
    <View style={styles.padding}>
      {paymentMethod ? (
        <PaymentMethodListItem
          testID="scan-to-pay.payments.selector"
          accessibilityLabel={t('account.payment.title')}
          accessibilityHint={t('account.payment.title')}
          iconName={getCreditCardIcon(paymentMethod.cardType)}
          title={paymentMethod.nickname}
          subtitle={paymentMethod.description}
          notice={notice}
          tag={paymentMethod.isDefault ? t('account.payment.default') : null}
          onPress={onSelect}
        />
      ) : (
        <PaymentMethodListItem
          testID="account.edit-payment.new"
          accessibilityLabel={t('account.payment.title')}
          accessibilityHint={t('account.payment.title')}
          iconName={getCreditCardIcon('NEW')}
          title={t('account.payment.new')}
          onPress={onSelect}
        />
      )}
    </View>
  );
};

function usePaymentMethodExpirationMessage(
  expirationState: PaymentMethod['expirationState'] = CardExpirationState.Ok,
) {
  const { t } = useLocalizationContext();

  if (expirationState === 'EXPIRED') return t('account.payment.expired');

  if (expirationState === 'NEARING') return t('account.payment.expiring');
}

type PaymentMethodSelectorProps = Readonly<{
  paymentMethods: readonly PaymentMethod[];
  onSelect: () => void;
}>;

const styles = StyleSheet.create({
  padding: {
    paddingHorizontal: theme.spacing['2'],
  },
});
